/**
 * The single sign-in.
 *
 * One login for accounting, HR, operations and Field Assist. Signing in here
 * establishes the session, the company, the financial year and the permission
 * set that the entire application then runs on.
 */

import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useSession } from '@/stores/session';

export function LoginPage() {
  const user = useSession((s) => s.user);
  const ready = useSession((s) => s.ready);
  const login = useSession((s) => s.login);
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (ready && user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/'} replace />;
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;

    setError(null);
    setBusy(true);
    try {
      await login(username.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-[380px]">
        <div className="mb-7 flex flex-col items-center text-center">
          <span
            aria-hidden
            className="mb-3 grid h-11 w-11 place-items-center rounded-xl bg-primary text-[17px] font-bold text-primary-foreground"
          >
            R
          </span>
          <h1 className="text-[19px] font-semibold tracking-tight">RSC ERP</h1>
          <p className="mt-1 text-[15px] text-muted-foreground">
            Sign in once for your shops, your stockists and your staff.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-xl border border-border bg-card p-6 shadow-raised"
          noValidate
        >
          {error && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-md border border-destructive/25 bg-destructive/[0.06] px-3 py-2.5 text-[15px] text-destructive"
            >
              <AlertCircle className="mt-px h-4 w-4 shrink-0" aria-hidden />
              <span>{error}</span>
            </div>
          )}

          <label htmlFor="username" className="mb-1.5 block text-[15px] font-medium">
            Username
          </label>
          <input
            id="username"
            name="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
            required
            disabled={busy}
            className="mb-4 h-10 w-full rounded-md border border-input bg-background px-3 text-[14px] outline-none transition-colors focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25 disabled:opacity-60"
          />

          <label htmlFor="password" className="mb-1.5 block text-[15px] font-medium">
            Password
          </label>
          <div className="relative mb-5">
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={busy}
              className="h-10 w-full rounded-md border border-input bg-background px-3 pr-10 text-[14px] outline-none transition-colors focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25 disabled:opacity-60"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-1 top-1 grid h-8 w-8 place-items-center rounded text-muted-foreground hover:bg-accent"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          <button
            type="submit"
            disabled={busy || !username.trim() || !password}
            className="flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/92 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-55"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="mt-5 text-center text-[14px] text-muted-foreground">
          Trouble signing in? Ask your administrator to check your account.
        </p>

        {/*
          * The version, before anybody signs in.
          *
          * It was only at the foot of the menu, which you cannot see until
          * you are in. So when somebody says "I see no changes" there was no
          * way for either of them to find out whether the phone was holding
          * an old copy — and a browser that has an app installed can hold one
          * for days. Now the answer is on the first screen.
          */}
        <p className="mt-6 text-center text-[12.5px] text-muted-foreground/70">
          Version {__BUILD_STAMP__}
        </p>
      </div>
    </div>
  );
}
